/**
 * Schémas Zod — fournisseurs et conditions d'achat par article (E09-03).
 * Référence : document 06 § 6.4.
 */
import { z } from 'zod';
import { paginationQuerySchema, uuidSchema } from './common.schema.js';
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
/** Montant : nombre ou chaîne décimale positive, jusqu'à 4 décimales. */
const amountSchema = z
    .union([z.number(), z.string().regex(/^\d{1,11}(\.\d{1,4})?$/, { message: 'Montant invalide.' })])
    .nullable()
    .optional();
/** Taux en pourcentage (0 à 100, deux décimales au plus). */
const ratePercentSchema = z
    .union([
    z.number(),
    z.string().regex(/^\d{1,3}(\.\d{1,2})?$/, { message: 'Taux invalide (pourcentage attendu).' }),
])
    .refine((value) => Number(value) >= 0 && Number(value) <= 100, {
    message: 'Le taux doit être compris entre 0 et 100.',
});
/** Quantité strictement positive, jusqu'à 4 décimales. */
const quantitySchema = z
    .union([
    z.number(),
    z.string().regex(/^\d{1,11}(\.\d{1,4})?$/, { message: 'Quantité invalide.' }),
])
    .refine((value) => Number(value) > 0, { message: 'La quantité doit être supérieure à zéro.' });
const optionalDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: 'Date invalide (format attendu : AAAA-MM-JJ).',
})
    .nullable()
    .optional();
/** Numéro composé d'exactement `length` chiffres, facultatif (`null` lorsqu'il est absent). */
const optionalDigits = (length, message) => z
    .string()
    .trim()
    .regex(new RegExp(`^\\d{${length}}$`), { message })
    .nullable()
    .optional();
// --- Fournisseurs ------------------------------------------------------------------------------
/** Paramètres de `GET /suppliers`. */
export const supplierListQuerySchema = paginationQuerySchema.extend({
    filter: z
        .object({
        isActive: z.enum(['true', 'false']).optional(),
        search: z.string().trim().max(180).optional(),
    })
        .optional(),
});
const supplierBaseSchema = {
    name: z.string().trim().min(1).max(180),
    // Facultatif : le code est dérivé du nom par l'API lorsqu'il n'est pas fourni.
    code: z.string().trim().min(1).max(40).optional(),
    contactName: optionalText(120),
    contactPhone: optionalText(30),
    contactEmail: z
        .string()
        .trim()
        .email({ message: 'Adresse e-mail invalide.' })
        .max(190)
        .nullable()
        .optional(),
    addressLine1: optionalText(180),
    addressLine2: optionalText(180),
    postalCode: optionalText(16),
    city: optionalText(120),
    countryCode: optionalText(2),
    siren: optionalDigits(9, 'Le SIREN doit comporter 9 chiffres.'),
    siret: optionalDigits(14, 'Le SIRET doit comporter 14 chiffres.'),
    vatNumber: optionalText(20),
    paymentTermId: uuidSchema.nullable().optional(),
    comment: optionalText(500),
    isActive: z.boolean().optional(),
};
/** Corps de `POST /suppliers`. */
export const supplierCreateSchema = z.object(supplierBaseSchema);
/** Corps de `PATCH /suppliers/:id`. */
export const supplierUpdateSchema = z
    .object(supplierBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
// --- Conditions d'achat par article ------------------------------------------------------------
/** Paramètres d'URL `:itemId`. */
export const itemIdParamSchema = z.object({ itemId: uuidSchema });
/** Paramètres d'URL `:itemId` et `:id`. */
export const itemSupplierIdParamSchema = z.object({ itemId: uuidSchema, id: uuidSchema });
const discountSchema = z.object({
    position: z.number().int().min(0).max(32767).optional(),
    rate: ratePercentSchema,
    label: optionalText(60),
});
const quantityTierSchema = z.object({
    minQuantity: quantitySchema,
    rate: ratePercentSchema,
    label: optionalText(60),
});
const itemSupplierBaseSchema = {
    supplierId: uuidSchema,
    supplierReference: optionalText(60),
    /** Prix tarif HT du fournisseur (prérempli avec le prix public de l'article). */
    basePriceHt: amountSchema,
    /** Prix net HT communiqué directement par le fournisseur ; il prime sur le tarif/remises. */
    netPriceHt: amountSchema,
    isPrimary: z.boolean().optional(),
    leadTimeDays: z.number().int().min(0).max(32767).nullable().optional(),
    shippingFeeHt: amountSchema,
    freeShippingThresholdHt: amountSchema,
    priceValidUntil: optionalDateSchema,
    comment: optionalText(500),
    isActive: z.boolean().optional(),
    discounts: z.array(discountSchema).max(10).optional(),
    quantityTiers: z.array(quantityTierSchema).max(20).optional(),
};
/** Corps de `POST /items/:itemId/suppliers`. */
export const itemSupplierCreateSchema = z.object(itemSupplierBaseSchema);
/** Corps de `PATCH /items/:itemId/suppliers/:id`. */
export const itemSupplierUpdateSchema = z
    .object(itemSupplierBaseSchema)
    .partial()
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
//# sourceMappingURL=supplier.schema.js.map